import React, { useEffect, useState, useCallback } from "react";
import { Coins, User, Mail, Phone, Calendar, Award, Activity, Zap, ShieldCheck, Copy, Check, RefreshCw, Bot, TrendingUp, AlertCircle, ExternalLink, Clock } from "lucide-react";
import Header from "../components/Header";
import { useAuth } from "../context/AuthContext";
import { useTheme, THEMES } from "../context/ThemeContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import PriorityCheckoutModal from "../components/PriorityCheckoutModal";

var NP_POS = "https://nowpayments.io/pos-terminal/gaurillabs";

// ── Verification Status Banner ───────────────────────────────────────────
function VerificationBanner(props) {
  var user  = props.user;
  var theme = props.theme;
  var isPending = !user.role || user.role === "user";
  if (!isPending) return null;
  return (
    <div className="mb-8 border p-6" style={{ borderColor: "#f59e0b40", background: "#f59e0b06" }}>
      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 flex items-center justify-center border border-amber-500/40 shrink-0">
            <Clock className="h-4 w-4 text-amber-400" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-amber-400 mb-1">Account Status</div>
            <h3 className="font-display font-bold text-lg text-white">Payment Verification Pending</h3>
            <p className="text-zinc-400 text-sm mt-1 max-w-lg">
              Your account is registered. Complete your crypto payment to activate Commander access.
              Use your email <span className="font-mono text-white">{user.email}</span> as the payment reference.
            </p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 shrink-0">
          <a href={NP_POS} target="_blank" rel="noreferrer"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 font-display font-bold text-sm tracking-widest uppercase transition-all hover:scale-105"
            style={{ backgroundColor: "#f59e0b", color: "#000" }}>
            Pay Now <ExternalLink className="h-3.5 w-3.5" />
          </a>
          <a href="/pending"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 font-display font-bold text-sm tracking-widest uppercase border border-amber-500/30 text-amber-400 hover:bg-amber-500/10 transition-all">
            View Instructions
          </a>
        </div>
      </div>
      <div className="mt-5 pt-5 border-t border-amber-500/15 grid sm:grid-cols-3 gap-4">
        {["Complete payment via NowPayments using your email as reference",
          "Admin verifies your payment within 24 hours",
          "Account activated — full Commander access granted"].map(function (text, i) {
          return (
            <div key={i} className="flex items-start gap-3">
              <div className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-black shrink-0 bg-amber-500/15 text-amber-400 border border-amber-500/30">
                {i + 1}
              </div>
              <p className="text-xs text-zinc-500 leading-relaxed">{text}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

var BACKEND   = process.env.REACT_APP_BACKEND_URL;
var MCP_URL   = "https://delightful-serenity-production-e319.up.railway.app/mcp";

// ICE_Man access requires BOTH:
// 1. Admin-approved account (role === "client")
// 2. Power tier (tier === "tier_9")
function canAccessICEMan(user) {
  return user && user.role === "client" && user.tier === "tier_9";
}
var COMMANDER = process.env.REACT_APP_COMMANDER_URL || "https://commander.gaurilabs.com";

var TIER_LABEL = {
  tier_3: { num: "3", label: "Strategy",  display: "Strategy" },
  tier_6: { num: "6", label: "Harmony",   display: "Harmony"  },
  tier_9: { num: "9", label: "Power",     display: "Power"    },
};

// ── ICEMan Tab ─────────────────────────────────────────────────────────────
function ICEManTab(props) {
  var user  = props.user;
  var theme = props.theme;
  var isPower = user.tier === "tier_9";

  var [portfolio,  setPortfolio]  = useState(null);
  var [treasury,   setTreasury]   = useState(null);
  var [loading,    setLoading]    = useState(true);
  var [apiError,   setApiError]   = useState(false);
  var [mcpCopied,  setMcpCopied]  = useState(false);
  var [stopping,   setStopping]   = useState({});

  var fetchData = useCallback(function () {
    if (!isPower || !user.id) { setLoading(false); return; }
    setLoading(true);
    setApiError(false);
    Promise.all([
      fetch(BACKEND + "/api/ice_man/portfolio/" + user.id),
      fetch(BACKEND + "/api/ice_man/treasury_balance"),
    ])
      .then(function (responses) {
        return Promise.all([responses[0].json(), responses[1].json()]);
      })
      .then(function (data) {
        setPortfolio(data[0]);
        setTreasury(data[1]);
        setLoading(false);
      })
      .catch(function () {
        setApiError(true);
        setLoading(false);
      });
  }, [user.id, isPower]);

  useEffect(function () { fetchData(); }, [fetchData]);

  function copyMCP() {
    navigator.clipboard.writeText(MCP_URL);
    setMcpCopied(true);
    setTimeout(function () { setMcpCopied(false); }, 2500);
  }

  function stopBot(botId) {
    setStopping(function (s) { var n = Object.assign({}, s); n[botId] = true; return n; });
    fetch(BACKEND + "/api/ice_man/stop_bot/" + botId, { method: "POST" })
      .then(function () { return fetchData(); })
      .catch(function () {})
      .finally(function () {
        setStopping(function (s) { var n = Object.assign({}, s); n[botId] = false; return n; });
      });
  }

  // Non-power gate
  if (!isPower) {
    return (
      <div className="mt-6">
        <div className="relative overflow-hidden border border-white/8 bg-[#060606] p-10 text-center">
          <div className="absolute inset-0 pointer-events-none"
            style={{ background: "radial-gradient(ellipse at 50% 50%, " + theme.color + "08, transparent 65%)" }} />
          <div className="relative z-10 max-w-md mx-auto">
            <div className="inline-flex items-center justify-center w-14 h-14 border mb-6"
              style={{ borderColor: theme.color + "30", color: theme.color }}>
              <Bot className="h-6 w-6" />
            </div>
            <h3 className="font-display font-black text-2xl tracking-tight mb-3">
              ICE_Man is a <span style={{ color: theme.color }}>Power</span> exclusive.
            </h3>
            <p className="text-zinc-400 text-sm leading-relaxed mb-8">
              Connect Claude.ai as your DeFi trading interface. Deploy bots,
              withdraw profits, and set alerts in natural language. Power tier only.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href="/login?tier=tier_9"
                className="px-8 py-3.5 font-display font-bold text-sm tracking-widest uppercase transition-all hover:scale-105"
                style={{ backgroundColor: theme.color, color: "#000", boxShadow: "0 0 32px " + theme.color + "40" }}>
                Upgrade to Power
              </a>
              <a href="/iceman"
                className="px-8 py-3.5 font-display font-bold text-sm tracking-widest uppercase border border-white/10 text-zinc-400 hover:text-white transition-all">
                Learn more
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  var bots         = (portfolio && portfolio.bots)     || [];
  var totalEarnings= (portfolio && portfolio.earnings)  || 0;
  var activeBots   = bots.filter(function (b) { return b.status === "active"; });
  var solPrice     = (treasury && treasury.sol_price_usd) || 0;
  var availableSOL = (treasury && treasury.available_sol)  || 0;

  return (
    <div className="mt-6 space-y-5">

      {/* MCP Connection Card */}
      <div className="border bg-[#060606] p-6"
        style={{ borderColor: theme.color + "30", boxShadow: "0 0 40px " + theme.color + "10" }}>
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
          <div className="flex items-start gap-4">
            <div className="mt-1 w-3 h-3 rounded-full shrink-0"
              style={{ backgroundColor: theme.color, boxShadow: "0 0 8px " + theme.color, animation: "pulse 2s ease-in-out infinite" }} />
            <div>
              <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">ICE_Man MCP Active</div>
              <h3 className="font-display font-bold text-lg" style={{ color: theme.color }}>Claude.ai Integration Ready</h3>
              <p className="text-zinc-400 text-sm mt-1">Claude.ai &rarr; Settings &rarr; Integrations &rarr; MCP Servers &rarr; paste URL</p>
            </div>
          </div>
          <div className="flex items-center gap-3 border border-white/10 bg-[#030303] px-4 py-3 w-full lg:w-auto lg:max-w-sm shrink-0">
            <code className="font-mono text-xs text-zinc-300 truncate flex-1">{MCP_URL}</code>
            <button onClick={copyMCP}
              className="shrink-0 flex items-center gap-1.5 text-xs font-mono font-bold uppercase tracking-widest px-3 py-1.5 border transition-all"
              style={{ color: mcpCopied ? "#4ade80" : theme.color, borderColor: mcpCopied ? "#4ade8040" : theme.color + "40" }}>
              {mcpCopied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
              {mcpCopied ? "Copied!" : "Copy"}
            </button>
          </div>
        </div>
        <div className="mt-5 pt-5 border-t border-white/5 grid sm:grid-cols-3 gap-4">
          {[
            "Step 1: Open Claude.ai Settings and go to Integrations",
            "Step 2: Click Add MCP Server and paste the URL above",
            "Step 3: Open a new chat and type: Show my portfolio",
          ].map(function (text, i) {
            return (
              <div key={i} className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full flex items-center justify-center font-display font-black text-xs shrink-0"
                  style={{ background: theme.color + "15", color: theme.color, border: "1px solid " + theme.color + "30" }}>
                  {i + 1}
                </div>
                <p className="text-zinc-400 text-xs leading-relaxed">{text}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Stats row */}
      <div className="grid sm:grid-cols-3 gap-4">
        <div className="p-5 border border-white/8 bg-[#060606]">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500 mb-3">
            <TrendingUp className="h-3.5 w-3.5" style={{ color: theme.color }} />
            Total Earnings
          </div>
          {loading ? (
            <div className="h-8 w-24 bg-white/5 animate-pulse" />
          ) : (
            <div className="font-display font-black text-3xl" style={{ color: theme.color }}>
              ${typeof totalEarnings === "number" ? totalEarnings.toLocaleString() : "0"}
            </div>
          )}
          <div className="text-zinc-600 text-xs font-mono mt-1">all-time bot profits</div>
        </div>

        <div className="p-5 border border-white/8 bg-[#060606]">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500 mb-3">
            <Coins className="h-3.5 w-3.5" style={{ color: theme.color }} />
            Treasury Balance
          </div>
          {loading ? (
            <div className="h-8 w-24 bg-white/5 animate-pulse" />
          ) : (
            <div className="font-display font-black text-3xl" style={{ color: theme.color }}>
              {availableSOL ? availableSOL.toFixed(3) : "0.000"} <span className="text-sm font-normal text-zinc-400">SOL</span>
            </div>
          )}
          <div className="text-zinc-600 text-xs font-mono mt-1">
            approx ${solPrice && availableSOL ? (solPrice * availableSOL).toFixed(2) : "0.00"} USD
          </div>
        </div>

        <div className="p-5 border border-white/8 bg-[#060606]">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500 mb-3">
            <Bot className="h-3.5 w-3.5" style={{ color: theme.color }} />
            Active Bots
          </div>
          {loading ? (
            <div className="h-8 w-16 bg-white/5 animate-pulse" />
          ) : (
            <div className="font-display font-black text-3xl" style={{ color: theme.color }}>
              {activeBots.length} <span className="text-sm font-normal text-zinc-400">/ 10</span>
            </div>
          )}
          <div className="text-zinc-600 text-xs font-mono mt-1">running right now</div>
        </div>
      </div>

      {/* Bot List */}
      <div className="border border-white/8 bg-[#060606] overflow-hidden">
        <div className="px-5 py-4 border-b border-white/6 flex items-center justify-between">
          <h3 className="font-display font-bold text-sm uppercase tracking-widest">Active Bots</h3>
          <button onClick={fetchData} disabled={loading}
            className="flex items-center gap-1.5 text-xs font-mono text-zinc-500 hover:text-zinc-300 transition-colors disabled:opacity-40">
            <RefreshCw className={"h-3 w-3 " + (loading ? "animate-spin" : "")} />
            Refresh
          </button>
        </div>

        {apiError && (
          <div className="px-5 py-8 flex items-center gap-3 text-zinc-500">
            <AlertCircle className="h-4 w-4 text-amber-400 shrink-0" />
            <span className="text-sm">Could not reach ICE_Man backend.</span>
            <button onClick={fetchData} className="ml-2 text-xs underline text-zinc-400">Retry</button>
          </div>
        )}

        {!loading && !apiError && bots.length === 0 && (
          <div className="px-5 py-12 text-center">
            <Bot className="h-10 w-10 mx-auto mb-3 text-zinc-700" />
            <h4 className="font-display font-bold text-base mb-2">No bots deployed yet.</h4>
            <p className="text-zinc-500 text-sm mb-5 max-w-xs mx-auto">
              Open Claude.ai and say: <span className="font-mono" style={{ color: theme.color }}>"start_bot BONK 0.5 SOL"</span>
            </p>
            <a href="https://claude.ai" target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-display font-bold uppercase tracking-widest border transition-all hover:scale-105"
              style={{ borderColor: theme.color + "50", color: theme.color }}>
              Open Claude.ai <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </div>
        )}

        {!loading && !apiError && bots.length > 0 && (
          <div className="divide-y divide-white/4">
            {bots.map(function (bot) {
              return (
                <div key={bot.id} className="px-5 py-4 flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-2 h-2 rounded-full shrink-0"
                      style={{
                        backgroundColor: bot.status === "active" ? "#4ade80" : "#52525b",
                        boxShadow: bot.status === "active" ? "0 0 6px #4ade80" : "none",
                      }} />
                    <div>
                      <div className="font-display font-bold text-sm">{bot.token_pair || bot.token || "Bot"}</div>
                      <div className="text-zinc-500 text-xs font-mono mt-0.5">
                        {bot.type || "Volume Bot"} &middot; {bot.amount_sol ? bot.amount_sol + " SOL" : "—"}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-5">
                    <div className="text-right">
                      <div className="text-xs uppercase tracking-widest text-zinc-600 mb-0.5">Today</div>
                      <div className="font-display font-bold text-base"
                        style={{ color: (bot.earnings_today || 0) >= 0 ? "#4ade80" : "#f87171" }}>
                        {(bot.earnings_today || 0) >= 0 ? "+" : ""}${(bot.earnings_today || 0).toLocaleString()}
                      </div>
                    </div>
                    <span className="inline-flex items-center gap-1.5 px-3 h-7 border font-mono text-xs tracking-widest uppercase"
                      style={{
                        borderColor: bot.status === "active" ? "#4ade8040" : "rgba(255,255,255,0.1)",
                        color:       bot.status === "active" ? "#4ade80"    : "#71717a",
                      }}>
                      {bot.status}
                    </span>
                    {bot.status === "active" && (
                      <button onClick={function () { stopBot(bot.id); }} disabled={stopping[bot.id]}
                        className="px-4 py-1.5 text-xs font-mono font-bold uppercase tracking-widest border border-red-500/30 text-red-400 hover:bg-red-500/10 transition-all disabled:opacity-50">
                        {stopping[bot.id] ? "Stopping..." : "Stop"}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Open Claude shortcut */}
      <div className="flex items-center justify-between border border-white/5 bg-[#060606] px-5 py-4">
        <div>
          <div className="text-sm font-medium text-zinc-300">Control ICE_Man via Claude.ai</div>
          <div className="text-xs text-zinc-600 font-mono mt-0.5">Your MCP is connected. Open a chat to start trading.</div>
        </div>
        <a href="https://claude.ai" target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-2 px-5 py-2.5 font-display font-bold text-xs uppercase tracking-widest transition-all hover:scale-105"
          style={{ backgroundColor: theme.color, color: "#000" }}>
          Open Claude.ai <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </div>
    </div>
  );
}

// ── Main Dashboard ──────────────────────────────────────────────────────────
export default function UserDashboard() {
  var auth    = useAuth();
  var user    = auth.user;
  var themeCtx = useTheme();
  var theme   = themeCtx.theme;
  var [icoData,      setIcoData]      = useState(null);
  var [icoLoading,   setIcoLoading]   = useState(false);
  var [priorityOpen, setPriorityOpen] = useState(false);

  useEffect(function () {
    if (!user || !user.wallet_address) return;
    setIcoLoading(true);
    fetch(BACKEND + "/api/ico/purchases/" + user.wallet_address)
      .then(function (r) { return r.json(); })
      .then(function (data) { setIcoData(data); setIcoLoading(false); })
      .catch(function () { setIcoLoading(false); });
  }, [user]);

  if (!user) return null;

  var tier      = user.tier ? TIER_LABEL[user.tier] : null;
  var themeMeta = THEMES[user.theme] || THEMES.jupiter_gold;
  var isPower   = canAccessICEMan(user);

  var confirmedPurchases = (icoData && icoData.purchases)
    ? icoData.purchases.filter(function (p) { return p.status === "confirmed"; })
    : [];
  var totalGlabs  = (icoData && icoData.total_glabs) || 0;
  var totalBlocks = confirmedPurchases.reduce(function (s, p) { return s + (p.blocks || 0); }, 0);

  return (
    <div className="min-h-screen bg-[#030303] text-white">
      <Header />
      <main className="pt-24 pb-16 max-w-7xl mx-auto px-6 lg:px-8">

        {/* Verification banner — shows only for pending users */}
        <VerificationBanner user={user} theme={theme} />

        {/* Page header */}
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">My grid</div>
            <h1 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight">
              Hello, <span style={{ color: theme.color }}>{user.name || "Operator"}</span>.
            </h1>
            <p className="text-zinc-400 mt-2 text-sm">Your isolated workspace — credits, bots, and account details.</p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {tier && (
              <div className="flex items-center gap-3 px-5 h-14 border"
                style={{ borderColor: theme.color + "60", boxShadow: "0 0 20px " + theme.color + "20" }}>
                <div className="font-display font-black text-2xl leading-none" style={{ color: theme.color }}>{tier.num}</div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-zinc-500">Plan</div>
                  <div className="font-display font-bold text-sm" style={{ color: theme.color }}>{tier.display}</div>
                </div>
              </div>
            )}

            <div className="flex items-center gap-3 px-5 h-14 border"
              style={{ borderColor: theme.color, boxShadow: "0 0 24px " + theme.color + "44" }}
              data-testid="dashboard-credits-card">
              <Coins className="h-5 w-5" style={{ color: theme.color }} />
              <div>
                <div className="text-xs uppercase tracking-widest text-zinc-500">Balance</div>
                <div className="font-display font-black text-xl">
                  {user.credits} <span className="text-xs font-normal text-zinc-400">Credits</span>
                </div>
              </div>
            </div>

            {totalGlabs > 0 && (
              <div className="flex items-center gap-3 px-5 h-14 border"
                style={{ borderColor: "#F5A623", boxShadow: "0 0 24px #F5A62344" }}>
                <Zap className="h-5 w-5" style={{ color: "#F5A623" }} />
                <div>
                  <div className="text-xs uppercase tracking-widest text-zinc-500">GLABS</div>
                  <div className="font-display font-black text-xl">
                    {totalGlabs.toLocaleString()} <span className="text-xs font-normal text-zinc-400">tokens</span>
                  </div>
                </div>
              </div>
            )}

            {user.has_priority_access && (
              <div className="flex items-center gap-2 px-4 h-14 border"
                style={{ borderColor: "#4CAF50", boxShadow: "0 0 20px #4CAF5044" }}>
                <ShieldCheck className="h-5 w-5" style={{ color: "#4CAF50" }} />
                <div>
                  <div className="text-xs uppercase tracking-widest text-zinc-500">Status</div>
                  <div className="text-sm font-bold" style={{ color: "#4CAF50" }}>Premium</div>
                </div>
              </div>
            )}

            {isPower && (
              <div className="flex items-center gap-2 px-4 h-14 border"
                style={{ borderColor: theme.color + "60", background: theme.color + "08" }}>
                <Bot className="h-5 w-5" style={{ color: theme.color }} />
                <div>
                  <div className="text-xs uppercase tracking-widest text-zinc-500">ICE_Man</div>
                  <div className="text-sm font-bold" style={{ color: theme.color }}>MCP Active</div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="details" className="w-full">
          <TabsList className="bg-black/40 border border-white/10" data-testid="dashboard-tabs">
            <TabsTrigger value="details" data-testid="tab-details"
              className="data-[state=active]:bg-[var(--theme-primary)] data-[state=active]:text-black">
              <User className="h-3.5 w-3.5 mr-1.5" /> My Details
            </TabsTrigger>
            <TabsTrigger value="waitlist" data-testid="tab-waitlist"
              className="data-[state=active]:bg-[var(--theme-primary)] data-[state=active]:text-black">
              <Activity className="h-3.5 w-3.5 mr-1.5" /> Plan Status
            </TabsTrigger>
            <TabsTrigger value="ico" data-testid="tab-ico"
              className="data-[state=active]:bg-[var(--theme-primary)] data-[state=active]:text-black">
              <Zap className="h-3.5 w-3.5 mr-1.5" /> GLabs Tokens
            </TabsTrigger>
            <TabsTrigger value="iceman" data-testid="tab-iceman"
              className="data-[state=active]:bg-[var(--theme-primary)] data-[state=active]:text-black">
              <Bot className="h-3.5 w-3.5 mr-1.5" /> ICE_Man
              {!isPower && (
                <span className="ml-1.5 px-1.5 py-0.5 text-xs font-mono uppercase border"
                  style={{ borderColor: theme.color + "40", color: theme.color, fontSize: "8px" }}>
                  Power
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Details */}
          <TabsContent value="details" className="mt-6">
            <div className="grid md:grid-cols-3 gap-4">
              <DetailRow icon={<User className="h-4 w-4" />}     label="Name"        value={user.name || "—"}                           testid="detail-name"     />
              <DetailRow icon={<Mail className="h-4 w-4" />}     label="Email"       value={user.email}                                 testid="detail-email"    />
              <DetailRow icon={<Phone className="h-4 w-4" />}    label="Mobile"      value={user.mobile || "—"}                         testid="detail-mobile"   />
              <DetailRow icon={<Award className="h-4 w-4" />}    label="Power Color" value={themeMeta.planet + " - " + themeMeta.name} color={themeMeta.color}   testid="detail-theme"    />
              <DetailRow icon={<Calendar className="h-4 w-4" />} label="Joined"      value={formatDate(user.created_at)}                testid="detail-joined"   />
              <DetailRow icon={<Activity className="h-4 w-4" />} label="Referral"    value={user.referral || "—"}                       testid="detail-referral" />
            </div>
          </TabsContent>

          {/* Plan Status */}
          <TabsContent value="waitlist" className="mt-6">
            <div className="p-8 border bg-[#0A0A0A]"
              style={{ borderColor: theme.color, boxShadow: "0 0 30px " + theme.color + "33" }}
              data-testid="waitlist-status-card">
              {tier ? (
                <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
                  <div className="font-display font-black leading-none" style={{ color: theme.color, fontSize: "120px" }}>
                    {tier.num}
                  </div>
                  <div className="flex-1">
                    <div className="text-xs uppercase tracking-widest text-zinc-500">Current Plan</div>
                    <h3 className="font-display font-black text-3xl tracking-tight mt-1">{tier.display}</h3>
                    <p className="text-zinc-400 mt-3 max-w-xl">
                      You are on the <span className="font-semibold" style={{ color: theme.color }}>{tier.display}</span> plan.
                      We will notify you via email when your node activates.
                    </p>
                    <div className="mt-6 flex flex-wrap items-center gap-3">
                      <StatusPill label="WAITLISTED" color={theme.color} />
                      <span className="text-xs text-zinc-500 font-mono">Position: early-access queue</span>
                    </div>
                    {!user.has_priority_access && (
                      <div className="mt-8 p-5 border border-dashed border-amber-500/40 bg-amber-500/5">
                        <div className="flex flex-wrap items-center justify-between gap-4">
                          <div>
                            <div className="text-xs uppercase tracking-widest text-amber-400 mb-1">Priority Premium</div>
                            <p className="text-sm text-zinc-300">Secure your spot for <span className="text-amber-400 font-semibold">2 months free Premium</span> at launch.</p>
                            <p className="text-xs text-zinc-500 mt-1">One-time $50 - USDT, BTC, or ETH</p>
                          </div>
                          <button onClick={function () { setPriorityOpen(true); }}
                            className="shrink-0 px-6 h-11 font-display font-bold text-sm tracking-wide"
                            style={{ background: "#F5A623", color: "#000" }}>
                            UPGRADE - $50
                          </button>
                        </div>
                      </div>
                    )}
                    {user.has_priority_access && (
                      <div className="mt-8 p-5 border border-green-500/40 bg-green-500/5 flex items-center gap-3">
                        <ShieldCheck className="h-6 w-6 text-green-400 shrink-0" />
                        <div>
                          <div className="text-sm font-bold text-green-400">Priority Premium active</div>
                          <p className="text-xs text-zinc-400 mt-0.5">Your 2-month free Premium node is secured.</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div>
                  <h3 className="font-display font-black text-xl">No plan selected yet.</h3>
                  <p className="text-zinc-400 mt-2 text-sm">Return to the landing page and choose Harmony or Power.</p>
                  {!user.has_priority_access && (
                    <div className="mt-6 p-5 border border-dashed border-amber-500/40 bg-amber-500/5">
                      <div className="flex flex-wrap items-center justify-between gap-4">
                        <div>
                          <div className="text-xs uppercase tracking-widest text-amber-400 mb-1">Priority Premium</div>
                          <p className="text-sm text-zinc-300">Secure your spot for <span className="text-amber-400 font-semibold">2 months free Premium</span> at launch.</p>
                          <p className="text-xs text-zinc-500 mt-1">One-time $50 - USDT, BTC, or ETH</p>
                        </div>
                        <button onClick={function () { setPriorityOpen(true); }}
                          className="shrink-0 px-6 h-11 font-display font-bold text-sm tracking-wide"
                          style={{ background: "#F5A623", color: "#000" }}>
                          UPGRADE - $50
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </TabsContent>

          {/* ICO */}
          <TabsContent value="ico" className="mt-6">
            <div className="space-y-6">
              <div className="grid md:grid-cols-3 gap-4">
                <div className="p-5 border border-white/10 bg-[#0A0A0A]">
                  <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Total blocks owned</div>
                  <div className="font-display font-black text-3xl" style={{ color: "#F5A623" }}>{totalBlocks}</div>
                </div>
                <div className="p-5 border border-white/10 bg-[#0A0A0A]">
                  <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Total GLABS tokens</div>
                  <div className="font-display font-black text-3xl" style={{ color: "#F5A623" }}>{totalGlabs.toLocaleString()}</div>
                </div>
                <div className="p-5 border border-white/10 bg-[#0A0A0A]">
                  <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Total invested</div>
                  <div className="font-display font-black text-3xl" style={{ color: "#F5A623" }}>${(totalBlocks * 1000).toLocaleString()}</div>
                </div>
              </div>

              {icoLoading ? (
                <div className="p-8 text-center text-zinc-500 text-sm">Loading your token holdings...</div>
              ) : confirmedPurchases.length === 0 ? (
                <div className="p-8 border border-white/10 bg-[#0A0A0A] text-center">
                  <Zap className="h-10 w-10 mx-auto mb-3 text-zinc-600" />
                  <h3 className="font-display font-bold text-lg mb-2">No GLabs tokens yet</h3>
                  <p className="text-zinc-400 text-sm mb-6">Purchase blocks from the ICO section on the home page.</p>
                  <a href="/#ico" className="inline-flex items-center gap-2 px-6 h-11 font-display font-bold text-sm"
                    style={{ background: "#F5A623", color: "#000" }}>
                    BUY GLABS TOKENS
                  </a>
                </div>
              ) : (
                <div className="border border-white/10 bg-[#0A0A0A] overflow-hidden">
                  <div className="px-5 py-4 border-b border-white/10">
                    <h3 className="font-display font-bold text-sm uppercase tracking-widest">Purchase History</h3>
                  </div>
                  <div className="divide-y divide-white/5">
                    {confirmedPurchases.map(function (p, i) {
                      return (
                        <div key={i} className="px-5 py-4 flex flex-wrap items-center justify-between gap-4">
                          <div>
                            <div className="font-mono text-xs text-zinc-500 mb-1">Order: {p.order_id || "—"}</div>
                            <div className="text-sm font-medium">
                              <span style={{ color: "#F5A623" }}>{p.blocks} block{p.blocks > 1 ? "s" : ""}</span>
                              <span className="text-zinc-400"> - {(p.glabs_amount || 0).toLocaleString()} GLABS</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <div className="text-xs text-zinc-500">Amount paid</div>
                              <div className="text-sm font-bold">${(p.blocks * 1000).toLocaleString()} USDT</div>
                            </div>
                            <span className="inline-flex items-center gap-1.5 px-3 h-7 border border-green-500/40 text-green-400 font-mono text-xs tracking-widest">
                              CONFIRMED
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {!user.wallet_address && (
                <div className="p-5 border border-amber-500/20 bg-amber-500/5 text-sm text-zinc-400">
                  <span className="text-amber-400 font-semibold">Note:</span> Connect your Phantom wallet on the home page ICO section to purchase blocks.
                </div>
              )}
            </div>
          </TabsContent>

          {/* ICEMan */}
          <TabsContent value="iceman" className="mt-0">
            <ICEManTab user={user} theme={theme} />
          </TabsContent>
        </Tabs>
      </main>

      <PriorityCheckoutModal open={priorityOpen} onClose={function () { setPriorityOpen(false); }} />
    </div>
  );
}

function DetailRow(props) {
  return (
    <div className="p-5 border border-white/10 bg-[#0A0A0A]" data-testid={props.testid}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500 mb-2">
        <span className="theme-text">{props.icon}</span> {props.label}
      </div>
      <div className="text-sm font-medium flex items-center gap-2">
        {props.color && <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: props.color }} />}
        <span className="break-words">{props.value}</span>
      </div>
    </div>
  );
}

function StatusPill(props) {
  return (
    <span className="inline-flex items-center gap-1.5 px-3 h-7 border font-mono text-xs tracking-widest"
      style={{ borderColor: props.color || "var(--theme-primary)", color: props.color || "var(--theme-primary)" }}>
      <span className="h-1.5 w-1.5 rounded-full pulse-glow" style={{ backgroundColor: props.color || "var(--theme-primary)" }} />
      {props.label}
    </span>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch (e) { return iso; }
}
